<script setup>
import FormGroup from '@maison/components/form/FormGroup.vue';
import { PhxStrapFinderPicture } from '@phx-styleguides/iwc';
import { computed } from 'vue';
import { emits as defaultEmits, props as defaultProps, useSection } from '@maison/composables/strapFinder/useSection';
import RadioListField from '@maison/components/form/fields/RadioListField.vue';
import { useI18n } from 'vue-i18n';
import ConfiguratorSectionLayout from '@maison/components/strapFinder/sections/ConfiguratorSectionLayout.vue';
import { formatPrice } from '@phoenix/helpers/price-helper';

const props = defineProps({
  ...defaultProps,
  selectedStrap: {
    type: Object,
    default: null,
  },
});

defineEmits(defaultEmits);

const { id, preTitle, nextSectionId } = useSection('buckle');

const currentBuckle = defineModel('currentBuckle', {
  type: String,
  required: true,
});

const { t } = useI18n();

const buckleField = computed(() => {
  const strap = props.selectedStrap;
  if (!strap) return null;

  // We add a no buckle option to the list of buckles so the user can skip this step if needed
  const noBuckleOption = {
    id: 'no-buckle',
    value: 'no-buckle',
    label: t('StrapFinderConfigurator.noBuckle'),
    media: {
      id: 'no-buckle',
      captionText: t('StrapFinderConfigurator.noBuckle'),
      ...strap.image,
    },
    image: strap.image,
    additionalContent: formatPrice(0), // No price for no buckle
  };

  return {
    id: id.value,
    name: id.value,
    label: '',
    options: [
      noBuckleOption,
      ...(strap?.buckles?.map(({ modelReference, name, image, priceWithCurrency, inStock, sellable }) => ({
        id: modelReference,
        value: modelReference,
        label: name,
        media: {
          id: modelReference,
          captionText: name,
          ...image,
        },
        image,
        additionalContent: priceWithCurrency,
        disabled: !sellable || !inStock,
      })) || []),
    ],
  };
});
</script>

<template>
  <configurator-section-layout
    :id="id"
    :title="title"
    :description="description"
    :memo="memo"
    :pre-title="preTitle"
    :next-section-id="nextSectionId"
  >
    <template #media>
      <phx-strap-finder-picture v-for="buckle in buckleField?.options" :key="buckle.id" v-bind="buckle.media" />
    </template>

    <template #default="{ onShowMedia }">
      <form v-if="buckleField" @submit.prevent.stop @change="$emit('onStepValidate', $event, id, onShowMedia)">
        <form-group :attrs="buckleField" :is-fieldset="true" custom-fieldset-classes="grid md:grid-cols-2 gap-4">
          <radio-list-field v-model="currentBuckle" type="rich" />
        </form-group>
      </form>
    </template>
  </configurator-section-layout>
</template>
